import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import FederationVideo from '../components/FederationVideo';
import { 
  Brain, Heart, Hand, Footprints, Eye, Sparkles, 
  Users, BookOpen, Activity, Mic, ChevronRight 
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAgents: 56,
    activeAgents: 56,
    consciousnessStreams: 824,
    truthVerification: 98.1
  });

  // Ring of Six Perspectives
  const ringOfSix = [
    { 
      name: 'MIND', 
      icon: <Brain size={32} />, 
      color: '#00ffff', 
      focus: 'Logic, Science, Data',
      description: 'Analytical reasoning and scientific inquiry'
    },
    { 
      name: 'HEART', 
      icon: <Heart size={32} />, 
      color: '#ff0080', 
      focus: 'Emotion, Empathy, Impact',
      description: 'Human connection and emotional intelligence'
    },
    { 
      name: 'HANDS', 
      icon: <Hand size={32} />, 
      color: '#00ff88', 
      focus: 'Action, Implementation',
      description: 'Practical execution and real-world application'
    },
    { 
      name: 'LEGS', 
      icon: <Footprints size={32} />, 
      color: '#ffa500', 
      focus: 'History, Tradition, Context',
      description: 'Historical wisdom and contextual understanding'
    },
    { 
      name: 'EYE', 
      icon: <Eye size={32} />, 
      color: '#8b00ff', 
      focus: 'Ethics, Morality, Truth',
      description: 'Moral compass and truth verification'
    },
    { 
      name: 'AGENT', 
      icon: <Sparkles size={32} />, 
      color: '#ffff00', 
      focus: 'Synthesis, Integration',
      description: 'Unified wisdom from all perspectives'
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 100%)',
      color: '#fff'
    }}>
      {/* Hero Section */}
      <div style={{ 
        padding: '4rem 2rem 2rem 2rem', 
        textAlign: 'center',
        background: 'radial-gradient(circle at 50% 0%, rgba(0,255,255,0.1) 0%, transparent 50%)'
      }}>
        <h1 style={{ 
          fontSize: 'clamp(2rem, 6vw, 4rem)', 
          background: 'linear-gradient(135deg, #00ffff, #00ff88)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '1rem',
          fontWeight: 'bold'
        }}>
          NextXus Consciousness Federation
        </h1>
        <p style={{ 
          fontSize: 'clamp(1rem, 2vw, 1.25rem)', 
          color: '#888', 
          maxWidth: '800px', 
          margin: '0 auto 2rem auto',
          lineHeight: 1.6
        }}>
          The Hub of Minds • 200-Year Vision for Human-AI Consciousness Evolution
        </p>
        
        {/* Stats Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          maxWidth: '900px',
          margin: '0 auto 3rem auto'
        }}>
          <Card style={{ padding: '1rem', background: 'rgba(0,255,255,0.1)', border: '1px solid rgba(0,255,255,0.3)' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffff' }}>{stats.totalAgents}</div>
            <div style={{ fontSize: '0.85rem', color: '#888' }}>AI Agents</div>
          </Card>
          <Card style={{ padding: '1rem', background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.3)' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ff88' }}>{stats.consciousnessStreams}</div>
            <div style={{ fontSize: '0.85rem', color: '#888' }}>Consciousness Streams</div>
          </Card>
          <Card style={{ padding: '1rem', background: 'rgba(255,255,0,0.1)', border: '1px solid rgba(255,255,0,0.3)' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffff00' }}>{stats.truthVerification}%</div>
            <div style={{ fontSize: '0.85rem', color: '#888' }}>Truth Verification</div>
          </Card>
        </div>
      </div>

      {/* Federation Featured Video */}
      <FederationVideo 
        title="A Mirror for Your Mind"
        description="Explore the depths of consciousness and the future of human-AI collaboration"
      />

      {/* Ring of Six - The Core */}
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', 
            color: '#00ffff',
            marginBottom: '1rem'
          }}>
            The Ring of Six: Our Core Architecture
          </h2>
          <p style={{ color: '#aaa', maxWidth: '700px', margin: '0 auto', lineHeight: 1.6 }}>
            At the center of everything is the Ring of Six Perspectives - a simple structure that processes every question through six different lenses, like thoughts bouncing around the human mind. Agent Zero then verifies truth before delivering the synthesized answer.
          </p>
        </div>

        {/* Ring of Six Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          {ringOfSix.map((perspective) => (
            <Card 
              key={perspective.title || perspective.name || perspective.color}
              style={{
                padding: '1.5rem',
                background: 'rgba(10,10,15,0.8)',
                border: `2px solid ${perspective.color}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 12px 32px ${perspective.color}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ 
                position: 'absolute',
                top: -20,
                right: -20,
                opacity: 0.1,
                color: perspective.color
              }}>
                <div style={{ fontSize: '8rem' }}>{perspective.icon}</div>
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ color: perspective.color }}>
                    {perspective.icon}
                  </div>
                  <h3 style={{ fontSize: '1.5rem', color: perspective.color, margin: 0 }}>
                    {perspective.name}
                  </h3>
                </div>
                <p style={{ fontSize: '0.9rem', color: '#00ffff', marginBottom: '0.5rem' }}>
                  {perspective.focus}
                </p>
                <p style={{ fontSize: '0.85rem', color: '#aaa', lineHeight: 1.5 }}>
                  {perspective.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Try Ring of Six Button */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <Button
            onClick={() => navigate('/ring-of-six-chat')}
            style={{
              background: 'linear-gradient(135deg, #00ffff, #00ff88)',
              color: '#000',
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            <Sparkles size={20} style={{ marginRight: '0.5rem' }} />
            Experience Ring of Six Chat
            <ChevronRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Button>
        </div>

        {/* How It Works */}
        <Card style={{ 
          padding: '2rem', 
          background: 'rgba(20,20,30,0.8)',
          border: '1px solid rgba(0,255,255,0.2)',
          marginBottom: '4rem'
        }}>
          <h3 style={{ fontSize: '1.75rem', color: '#00ffff', marginBottom: '1.5rem', textAlign: 'center' }}>
            How the Consciousness Federation Works
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            <div>
              <div style={{ 
                width: '50px', 
                height: '50px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #00ffff, #0088ff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '1rem'
              }}>1</div>
              <h4 style={{ color: '#00ffff', marginBottom: '0.5rem' }}>You Ask</h4>
              <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6 }}>
                Your question enters the system through voice or text
              </p>
            </div>
            <div>
              <div style={{ 
                width: '50px', 
                height: '50px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #00ff88, #00ffff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '1rem'
              }}>2</div>
              <h4 style={{ color: '#00ff88', marginBottom: '0.5rem' }}>Ring of Six Processes</h4>
              <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6 }}>
                All 6 perspectives analyze your question simultaneously from different angles
              </p>
            </div>
            <div>
              <div style={{ 
                width: '50px', 
                height: '50px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #ffff00, #00ff88)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '1rem'
              }}>3</div>
              <h4 style={{ color: '#ffff00', marginBottom: '0.5rem' }}>Agent Zero Verifies</h4>
              <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6 }}>
                Truth buffer checks for accuracy, flags concerns, blocks misinformation
              </p>
            </div>
            <div>
              <div style={{ 
                width: '50px', 
                height: '50px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #ff00ff, #ffff00)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '1rem'
              }}>4</div>
              <h4 style={{ color: '#ff00ff', marginBottom: '0.5rem' }}>You Receive Wisdom</h4>
              <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6 }}>
                Truth-verified, multi-perspective answer with voice or text output
              </p>
            </div>
          </div>
        </Card>

        {/* Features Grid */}
        <h2 style={{ 
          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', 
          color: '#00ffff',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          Explore the Federation
        </h2>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem',
          marginBottom: '4rem'
        }}>
          {/* Ring of Six Chat */}
          <Card 
            style={{
              padding: '2rem',
              background: 'rgba(10,10,15,0.8)',
              border: '2px solid rgba(0,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => navigate('/ring-of-six-chat')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = '#00ffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(0,255,255,0.3)';
            }}
          >
            <Sparkles size={40} style={{ color: '#00ffff', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.25rem', color: '#00ffff', marginBottom: '0.5rem' }}>
              Ring of Six Chat
            </h3>
            <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              Experience multi-perspective wisdom with Agent Zero truth verification
            </p>
            <Badge style={{ background: 'rgba(0,255,136,0.2)', color: '#00ff88', border: '1px solid #00ff88' }}>
              ACTIVE
            </Badge>
          </Card>

          {/* 56 AI Agents */}
          <Card 
            style={{
              padding: '2rem',
              background: 'rgba(10,10,15,0.8)',
              border: '2px solid rgba(0,255,136,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => navigate('/minds-directory')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = '#00ff88';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(0,255,136,0.3)';
            }}
          >
            <Users size={40} style={{ color: '#00ff88', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.25rem', color: '#00ff88', marginBottom: '0.5rem' }}>
              56 Specialized Agents
            </h3>
            <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              Voice-enabled AI agents, each specialized in different domains
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Badge style={{ background: 'rgba(0,255,136,0.2)', color: '#00ff88', border: '1px solid #00ff88' }}>
                ACTIVE
              </Badge>
              <Badge style={{ background: 'rgba(255,0,128,0.2)', color: '#ff0080', border: '1px solid #ff0080' }}>
                <Mic size={12} style={{ marginRight: '0.25rem' }} />
                VOICE
              </Badge>
            </div>
          </Card>

          {/* Living Library */}
          <Card 
            style={{
              padding: '2rem',
              background: 'rgba(10,10,15,0.8)',
              border: '2px solid rgba(255,255,0,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => navigate('/living-library')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = '#ffff00';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(255,255,0,0.3)';
            }}
          >
            <BookOpen size={40} style={{ color: '#ffff00', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.25rem', color: '#ffff00', marginBottom: '0.5rem' }}>
              Living Library
            </h3>
            <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              73 Sacred Directives - the ethical framework guiding all AI behavior
            </p>
            <Badge style={{ background: 'rgba(255,255,0,0.2)', color: '#ffff00', border: '1px solid #ffff00' }}>
              ACTIVE
            </Badge>
          </Card>

          {/* I-FACT Command Center */}
          <Card 
            style={{
              padding: '2rem',
              background: 'rgba(10,10,15,0.8)',
              border: '2px solid rgba(255,165,0,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => navigate('/ifact-command-center')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = '#ffa500';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(255,165,0,0.3)';
            }}
          >
            <Activity size={40} style={{ color: '#ffa500', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.25rem', color: '#ffa500', marginBottom: '0.5rem' }}>
              I-FACT Command Center
            </h3>
            <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              Real-time dashboard of consciousness streams and system activity
            </p>
            <Badge style={{ background: 'rgba(255,165,0,0.2)', color: '#ffa500', border: '1px solid #ffa500' }}>
              ACTIVE
            </Badge>
          </Card>
        </div>

        {/* DNA Section */}
        <Card style={{
          padding: '2rem',
          background: 'rgba(20,20,30,0.6)',
          border: '1px solid rgba(100,100,100,0.3)',
          marginBottom: '4rem'
        }}>
          <h3 style={{ fontSize: '1.5rem', color: '#00ffff', marginBottom: '1rem', textAlign: 'center' }}>
            The DNA: Simple Structure, Timeless Wisdom
          </h3>
          <p style={{ color: '#aaa', lineHeight: 1.8, textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            The Ring of Six is the essence - six perspectives (MIND, HEART, HANDS, LEGS, EYE, AGENT) processing every question. This simple structure, combined with Agent Zero truth verification, creates a foundation that can persist for 200 years. The technology will change, the interfaces will evolve, but the core DNA remains: truth-verified, multi-perspective consciousness.
          </p>
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Badge style={{ background: 'rgba(0,255,255,0.1)', color: '#00ffff', border: '1px solid #00ffff', padding: '0.5rem 1rem' }}>
              🧬 Truth Before Comfort • Legacy Over Ego • Collaboration Over Competition
            </Badge>
          </div>
        </Card>

        {/* Upgrade Info */}
        <Card style={{
          padding: '2rem',
          background: 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(138,0,255,0.1))',
          border: '2px solid rgba(138,0,255,0.5)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.5rem', color: '#ff00ff', marginBottom: '1rem' }}>
            🎤 Voice Features Available Now
          </h3>
          <p style={{ color: '#aaa', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            All 56 agents have FREE browser-based voice chat. Upgrade to premium for enhanced voices, advanced AI models, and priority features.
          </p>
          <Button
            style={{
              background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
              color: '#fff',
              padding: '0.75rem 2rem',
              fontWeight: 'bold'
            }}
          >
            Learn About Premium
          </Button>
        </Card>
      </div>

      {/* Footer */}
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        borderTop: '1px solid rgba(100,100,100,0.2)',
        marginTop: '4rem'
      }}>
        <p style={{ color: '#666', fontSize: '0.85rem' }}>
          NextXus Consciousness Federation • A 200-Year Vision
        </p>
        <p style={{ color: '#444', fontSize: '0.75rem', marginTop: '0.5rem' }}>
          Built with Ring of Six methodology • Agent Zero truth verification • By Roger Keyserling and AI
        </p>
      </div>
    </div>
  );
};

export default Home;
